import React, { useState, useContext } from "react";
import Container from "Components/Container/Container";
import Input from "form/Inputs/Input";
import useInput from "form/Hooks/user-input";
import PasswordInput from "form/Inputs/PasswordInput";
import { Link, useNavigate } from "react-router-dom";
import Spinner from "Components/RequestHandler/Spinner";
import usePost from "Hooks/usePost";
import { StatusHandler } from "Components/ApiNotification";
import VerificationModal from "Components/Modal/VerificationModal";
import { LoginContext } from "context/Auth/LoginContext";
import { useTranslation } from "react-i18next";
const Register = () => {
  const navigate = useNavigate();
  const [isClicked, setIsClicked] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const { setIsLoggedIn, setUser, setIsVerified, setToken } =
    useContext(LoginContext);
  const { t } = useTranslation();
  const {
    value: emailInput,
    isValid: emailIsValid,
    inputChangeHandler: emailChangeHandler,
    reset: resetEmail,
  } = useInput(
    (value) =>
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value) &&
      value.trim() !== ""
  );

  const {
    value: passwordInput,
    isValid: passwordIsValid,
    inputChangeHandler: passwordChangeHandler,
    reset: resetPassword,
  } = useInput(
    (value) =>
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(
        value
      ) && value.trim() !== ""
  );

  const {
    value: confirmPasswordInput,
    isValid: confirmPasswordIsValid,
    inputChangeHandler: confirmPasswordChangeHandler,
    reset: resetConfirmPassword,
  } = useInput((value) => value === passwordInput && value.trim() !== "");

  const {
    value: fullNameInput,
    isValid: fullNameIsValid,
    inputChangeHandler: fullNameChangeHandler,
    reset: resetFullName,
  } = useInput((value) => value.trim() !== "");

  const {
    value: phoneInput,
    isValid: phoneIsValid,
    inputChangeHandler: phoneChangeHandler,
    reset: resetPhone,
  } = useInput((value) => value.trim() !== "");

  const { postData, isLoading, isError, data } = usePost();

  const clearInputs = () => {
    resetEmail();
    resetPassword();
    resetConfirmPassword();
    resetFullName();
    resetPhone();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsClicked(true);

    if (
      !emailIsValid ||
      !passwordIsValid ||
      !confirmPasswordIsValid ||
      !fullNameIsValid ||
      !phoneIsValid
    )
      return;

    const res = await postData("auth/register", {
      email: emailInput,
      fullName: fullNameInput,
      phoneNumber: phoneInput,
      password: passwordInput,
      confirmPassword: confirmPasswordInput,
    });

    if (res?.success && res?.status === 201) {
      setUserEmail(emailInput);
      setShowVerificationModal(true);
      setIsLoggedIn(true);
      setUser(res.data.user);
      setToken(res.data.token);
      clearInputs();
      setIsClicked(false);
    }
  };

  const handleVerificationLater = () => {
    setShowVerificationModal(false);
    navigate("/");
  };

  const handleVerification = () => {
    navigate("/verify-email", { state: { email: userEmail } });
    setShowVerificationModal(false);
  };

  return (
    <main>
      <Container>
        <div className="my-10  flex flex-col  items-center justify-center">
          <h1 className="text-4xl font-medium text-center font-title">
            {t("auth.registerTitle")}
          </h1>
          <StatusHandler error={isError} data={data} />
        </div>

        <form className="lg:w-1/2 flex flex-col gap-y-8 mx-auto font-text">
          <div className="flex flex-col gap-y-4">
            <span className="flex flex-col lg:flex-row gap-y-4 lg:gap-y-0 items-center gap-x-4">
              <Input
                type="text"
                label={t("common.fullName")}
                value={fullNameInput}
                onChange={(e) => {
                  fullNameChangeHandler(e);
                  setIsClicked(false);
                }}
                hasError={isClicked && !fullNameIsValid}
                errorMessage={t("auth.fullNameError")}
              />
              <Input
                type="tel"
                label={t("common.phone")}
                value={phoneInput}
                onChange={(e) => {
                  phoneChangeHandler(e);
                  setIsClicked(false);
                }}
                hasError={isClicked && !phoneIsValid}
                errorMessage={t("auth.phoneError")}
              />
            </span>

            <Input
              type="email"
              label={t("common.email")}
              value={emailInput}
              onChange={(e) => {
                emailChangeHandler(e);
                setIsClicked(false);
              }}
              hasError={isClicked && !emailIsValid}
              errorMessage={t("auth.emailError")}
            />

            <PasswordInput
              type="password"
              label={t("common.password")}
              value={passwordInput}
              onChange={(e) => {
                passwordChangeHandler(e);
                setIsClicked(false);
              }}
              hasError={isClicked && !passwordIsValid}
              errorMessage={t("auth.passwordError")}
            />

            <PasswordInput
              type="password"
              label={t("common.confirmPassword")}
              value={confirmPasswordInput}
              onChange={(e) => {
                confirmPasswordChangeHandler(e);
                setIsClicked(false);
              }}
              hasError={isClicked && !confirmPasswordIsValid}
              errorMessage={t("auth.confirmPasswordError")}
            />
          </div>
          <div className="flex flex-col">
            <button
              onClick={handleSubmit}
              className="bg-primary flex items-center justify-center w-full text-center mt-4 text-white px-4 py-3 text-2xl uppercase rounded-md"
            >
              {isLoading ? <Spinner isWhite={true} /> : t("auth.register")}
            </button>

            <p className="text-center mt-4">
              {t("common.alreadyHaveAccount")}
              <Link className="hover:underline" to="/login">
                {t("common.login")}
              </Link>
            </p>
          </div>
        </form>
        <VerificationModal
          isOpen={showVerificationModal}
          onClose={handleVerificationLater}
          onVerify={handleVerification}
          email={userEmail}
        />
      </Container>
    </main>
  );
};

export default Register;
